import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";

import { api, unwrap } from "@/api/client";
import { useMeta } from "@/api/meta";
import { useAuth } from "@/app/auth";
import { Field, PasswordInput } from "@/components/common/Field";
import { TimezoneSelect, browserTimezone } from "@/components/common/TimezoneSelect";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { languages, type Language } from "@/i18n";
import { errorText, fieldError } from "@/lib/errors";

import { AuthShell } from "./AuthShell";

export function SetupPage() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const meta = useMeta();
  const [form, setForm] = useState({
    setup_token: "",
    company_name: "",
    timezone: browserTimezone(),
    locale: (i18n.resolvedLanguage ?? "ru") as Language,
    full_name: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  if (auth.status === "authenticated") return <Navigate to="/" replace />;
  if (meta.isPending) {
    return (
      <AuthShell title={t("setup.title")}>
        <Skeleton className="h-40" />
      </AuthShell>
    );
  }
  if (meta.data && !meta.data.setup_required) return <Navigate to="/login" replace />;

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const res = unwrap(
        await api.POST("/api/v1/setup", {
          body: { ...form, company_name: form.company_name.trim(), full_name: form.full_name.trim() },
        }),
      );
      auth.signIn(res);
      void navigate("/", { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
    }
  };

  const fieldErr = (f: string) => fieldError(t, error, f);
  const hasFieldError = ["company_name", "full_name", "email", "password", "timezone"].some((f) =>
    fieldErr(f),
  );

  return (
    <AuthShell wide title={t("setup.title")} subtitle={t("setup.subtitle")}>
      <form className="flex flex-col gap-6" onSubmit={(e) => void submit(e)}>
        <fieldset className="flex flex-col gap-4">
          <legend className="mb-3 text-base font-semibold">{t("setup.company")}</legend>
          <Field label={t("setup.companyName")} error={fieldErr("company_name")}>
            <Input
              required
              autoFocus
              maxLength={120}
              autoComplete="organization"
              value={form.company_name}
              onChange={(e) => set("company_name", e.target.value)}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <Field label={t("setup.timezone")}>
              <TimezoneSelect value={form.timezone} onChange={(e) => set("timezone", e.target.value)} />
            </Field>
            <Field label={t("setup.language")}>
              <NativeSelect
                className="h-10"
                value={form.locale}
                onChange={(e) => {
                  set("locale", e.target.value as Language);
                  void i18n.changeLanguage(e.target.value);
                }}
              >
                {languages.map((l) => (
                  <option key={l} value={l}>
                    {t(`languages.${l}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>
        </fieldset>
        <fieldset className="flex flex-col gap-4 border-t pt-5">
          <legend className="sr-only">{t("setup.admin")}</legend>
          <h2 aria-hidden="true" className="text-base font-semibold">
            {t("setup.admin")}
          </h2>
          <Field label={t("setup.fullName")} error={fieldErr("full_name")}>
            <Input
              required
              maxLength={120}
              autoComplete="name"
              value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)}
            />
          </Field>
          <Field label={t("setup.email")} error={fieldErr("email")}>
            <Input
              type="email"
              required
              autoComplete="username"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </Field>
          <Field label={t("setup.password")} hint={t("password.hint")} error={fieldErr("password")}>
            <PasswordInput
              required
              minLength={10}
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
            />
          </Field>
        </fieldset>
        <div className="border-t pt-5">
          <Field label={t("setup.token")} hint={t("setup.tokenHint")}>
            <Input
              required
              autoComplete="off"
              spellCheck={false}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              className="font-mono uppercase"
              value={form.setup_token}
              onChange={(e) => set("setup_token", e.target.value)}
            />
          </Field>
        </div>
        {error !== null && !hasFieldError && <Alert variant="danger">{errorText(t, error)}</Alert>}
        <Button
          type="submit"
          size="lg"
          disabled={
            pending ||
            !form.company_name.trim() ||
            !form.full_name.trim() ||
            !form.email.includes("@") ||
            form.password.length < 10 ||
            !form.setup_token.trim()
          }
        >
          {t("setup.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
